import CancelIcon from "@mui/icons-material/Clear";
import { InputAdornment, Chip, Alert } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { fetchEmployeesEligibleToTrade } from "../api/trade";
import {
  DST_WARNING,
  FREEFORM_REASON_CHAR_LIMIT,
  SELECT_HOURS_LIST,
  SELECT_MINUTES_LIST,
  TRADE_PAGE_TYPE,
} from "../util/constants";
import { THREE_MONTHS_IN_FUTURE, TODAY, TWO_MONTHS_IN_PAST } from "../util/dateHelper";
import { convert_Luxon_To_DateFns } from "../util/dateTimeFormatter.js";
import { formatEmployeeIdFromKronos } from "../util/miscFormatter.js";
import StarsDatePicker from "./fundamentals/StarsDatePicker";
import UnionContractCutoffTimeReminder from "./UnionContractCutoffTimeReminder";
import { getEligibleEmployeeLabel } from "../util/tradeUtils";
import { EMPLOYEE_TEMP_LABEL } from "../util/constants";

const useStyles = makeStyles((theme) => ({
  messageBorder: {
    width: "148px",
  },
  warningFormat: {
    textAlign: "center",
    whiteSpace: "pre-line",
    float: "left",
    marginLeft: "15px",
  },
  datetimeInputGrid: {
    maxWidth: "100vw",
    height: "max-content",
  },
  dateInput: {
    maxWidth: "50vw",
    width: "195px",
  },
  dateInputBox: {
    display: "inline-block",
  },
  datetimeInputBox: {
    display: "inline-block",
  },
  timeInputBox: {
    display: "inline-block",
  },
  timeInput: {
    width: "70px",
    maxWidth: "20vw",
  },
  timeColonBox: {
    display: "inline-block",
    fontSize: "20px",
    lineHeight: "0",
    top: "18px",
    position: "relative",
  },
  tempChip: {
    marginLeft: "5px",
  },
}));

const TimeTradeForm = (props) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [eligibleEmployees, setEligibleEmployees] = useState([]);

  useEffect(() => {
    if (!props.isAdminCreateTrade) {
      setEligibleEmployees([]);
      setIsLoading(true);
      fetchEmployeesEligibleToTrade(props.station, props.jobCode, "TIME")
        .then((res) => res.json())
        .then((result) => {
          if (result.message) {
            console.error("Error in eligible employees pull.");
            setIsLoading(false);
          } else {
            result.results = cleanupEmployees(result.results);
            setIsLoading(false);
            setEligibleEmployees(result.results.sort((a, b) => -("" + b.FullName).localeCompare("" + a.FullName)));
          }
        });
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.station]);

  const cleanupEmployees = (eligibleEmployees) => {
    const newEligibleEmployees = [];
    for (let i = 0; i < eligibleEmployees.length; i++) {
      eligibleEmployees[i].PersonNumber = formatEmployeeIdFromKronos(eligibleEmployees[i].PersonNumber);
      if (eligibleEmployees[i].PersonNumber !== props.offeredEmployeeId) {
        newEligibleEmployees.push(eligibleEmployees[i]);
      }
    }
    return newEligibleEmployees;
  };

  const handleEmpTwoSelectChange = (event, newValue) => {
    props.onEmpTwoChange(event, newValue);
  };

  const handleOfferedShiftStartChange = (event) => {
    props.onOfferedShiftStartChange(event);
    props.onRequestedShiftStartChange(event);
  };

  const handleOfferedShiftStartHourChange = (event) => {
    props.onOfferedShiftStartHourChange(event);
  };

  const handleOfferedShiftStartMinuteChange = (event) => {
    props.onOfferedShiftStartMinuteChange(event);
  };

  const handleOfferedShiftEndChange = (event) => {
    props.onOfferedShiftEndChange(event);
  };

  const handleOfferedShiftEndHourChange = (event) => {
    props.onOfferedShiftEndHourChange(event);
  };

  const handleOfferedShiftEndMinuteChange = (event) => {
    props.onOfferedShiftEndMinuteChange(event);
  };

  const handleOfferedReliefShiftSelectChange = (event) => {
    props.onOfferedReliefShiftSelectChange(event);
  };

  const handleRequestedShiftStartChange = (event) => {
    props.onRequestedShiftStartChange(event);
  };

  const handleRequestedShiftStartHourChange = (event) => {
    props.onRequestedShiftStartHourChange(event);
  };

  const handleRequestedShiftStartMinuteChange = (event) => {
    props.onRequestedShiftStartMinuteChange(event);
  };

  const handleRequestedShiftEndChange = (event) => {
    props.onRequestedShiftEndChange(event);
  };

  const handleRequestedShiftEndHourChange = (event) => {
    props.onRequestedShiftEndHourChange(event);
  };

  const handleRequestedShiftEndMinuteChange = (event) => {
    props.onRequestedShiftEndMinuteChange(event);
  };

  const handleRequestedReliefShiftSelectChange = (event) => {
    props.onRequestedReliefShiftSelectChange(event);
  };

  const handleCommentsChange = (event) => {
    props.onCommentsChange(event);
  };

  const handleCommentsBlur = (event) => {
    props.onCommentsBlur(event);
  };

  const renderHours = () => {
    return SELECT_HOURS_LIST.map((hour) => {
      return (
        <MenuItem key={hour} value={hour}>
          {hour}
        </MenuItem>
      );
    });
  };

  const renderMinutes = () => {
    return SELECT_MINUTES_LIST.map((minute) => {
      return (
        <MenuItem key={minute} value={minute}>
          {minute}
        </MenuItem>
      );
    });
  };

  const CancelButtonEmpOne = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={props.onEmpOneClear}>
        <CancelIcon />
      </IconButton>
    );
  };
  const CancelButtonEmpTwo = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={props.onEmpTwoClear}>
        <CancelIcon />
      </IconButton>
    );
  };
  const CancelButtonComments = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={props.onCommentsClear}>
        <CancelIcon />
      </IconButton>
    );
  };

  return (
    <div className="App-contents" data-testid="time-trade-form">
      <Grid container>
        <Grid item xs={12}>
          {props.isAdminCreateTrade ? (
            <div>
              <Divider className="divider" />
              <div className="section-title-left">Employee #1</div>
              <Box mt={1} mb={1}>
                {props.offeredEmployeeIdErrors !== "" ? (
                  <TextField
                    className="text-field"
                    label="Employee ID"
                    variant="outlined"
                    value={props.offeredEmployeeId}
                    onChange={props.onEmpOneChange}
                    onBlur={props.onEmpOneBlur}
                    onKeyUp={props.onEmpOneEnterKeyPressed}
                    InputProps={{
                      endAdornment:
                        props.offeredEmployeeId !== "" ? (
                          <InputAdornment position="start">
                            <CancelButtonEmpOne />
                          </InputAdornment>
                        ) : (
                          ""
                        ),
                    }}
                    error
                    helperText={props.offeredEmployeeIdErrors}
                  />
                ) : (
                  <TextField
                    className="text-field"
                    label="Employee ID"
                    variant="outlined"
                    value={props.offeredEmployeeId}
                    onChange={props.onEmpOneChange}
                    onBlur={props.onEmpOneBlur}
                    onKeyUp={props.onEmpOneEnterKeyPressed}
                    InputProps={{
                      endAdornment:
                        props.offeredEmployeeId !== "" ? (
                          <InputAdornment position="start">
                            <CancelButtonEmpOne />
                          </InputAdornment>
                        ) : (
                          ""
                        ),
                    }}
                  />
                )}
              </Box>
              {props.offeredEmployeeSearchTriggered ? (
                props.offeredEmployeeSearchLoaded ? (
                  props.offeredEmployeeLastName !== "" &&
                  props.offeredEmployeeFirstName !== "" &&
                  props.offeredEmployeeStation !== "" &&
                  props.offeredEmployeeWorkgroup !== "" ? (
                    <Grid className="manage-access-employee-text" item xs={12}>
                      <div>
                        <span className="bold-text">Name:</span>{" "}
                        {props.offeredEmployeeLastName + ", " + props.offeredEmployeeFirstName}
                      </div>
                      {props.offeredEmployeeTempStation && (
                        <div>
                          <span className="bold-text">Temp Station:</span> {props.offeredEmployeeTempStation} (Active)
                        </div>
                      )}
                      <div>
                        <span className="bold-text">Home Station:</span> {props.offeredEmployeeHomeStation}
                      </div>
                      <div>
                        <span className="bold-text">Workgroup:</span> {props.offeredEmployeeWorkgroup}
                      </div>
                    </Grid>
                  ) : (
                    ""
                  )
                ) : (
                  <CircularProgress />
                )
              ) : (
                ""
              )}
            </div>
          ) : (
            <div>
              <Divider className="divider" />
              <div className="section-title-left">Shift Offered</div>
            </div>
          )}

          <Grid className={classes.datetimeInputGrid} item xs={12}>
            <Box className={classes.datetimeInputBox} m={1}>
              <StarsDatePicker
                label="Start Date/Time"
                date={props.offeredShiftStart}
                onChange={handleOfferedShiftStartChange}
                dateErrors={props.offeredShiftStartErrors}
                minDate={convert_Luxon_To_DateFns(
                  props.isAdminCreateTrade ? TWO_MONTHS_IN_PAST(props.station) : TODAY(props.station)
                )}
                maxDate={convert_Luxon_To_DateFns(THREE_MONTHS_IN_FUTURE(props.station))}
                className={classes.dateInput}
              />
            </Box>

            <Box className={classes.datetimeInputBox} mt={1} mb={1} mr={1}>
              <Grid container className={classes.dateInputBox}>
                {props.offeredShiftStartTimeErrors !== "" ? (
                  <Grid item xs={12} className={classes.messageBorder}>
                    <Box className={classes.timeInputBox}>
                      <FormControl variant="outlined">
                        <InputLabel id="offeredShiftStartHourSelectLabel" error>
                          Hr
                        </InputLabel>
                        <Select
                          labelId="offeredShiftStartHourSelectLabel"
                          id="offeredShiftStartHourSelectElement"
                          className={classes.timeInput}
                          value={props.offeredShiftStartHour}
                          onChange={handleOfferedShiftStartHourChange}
                          label="Hr"
                          error
                        >
                          <MenuItem value="" disabled>
                            <em>Hour</em>
                          </MenuItem>
                          {renderHours()}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box className={classes.timeColonBox}>:</Box>
                    <Box className={classes.timeInputBox}>
                      <FormControl variant="outlined">
                        <InputLabel id="offeredShiftStartMinuteSelectLabel" error>
                          Min
                        </InputLabel>
                        <Select
                          labelId="offeredShiftStartMinuteSelectLabel"
                          id="offeredShiftStartMinuteSelectElement"
                          className={classes.timeInput}
                          value={props.offeredShiftStartMinute}
                          onChange={handleOfferedShiftStartMinuteChange}
                          label="Min"
                          error
                        >
                          <MenuItem value="" disabled>
                            <em>Minute</em>
                          </MenuItem>
                          {renderMinutes()}
                        </Select>
                      </FormControl>
                    </Box>
                    <FormHelperText className={classes.warningFormat} error>
                      {props.offeredShiftStartTimeErrors}
                    </FormHelperText>
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <Box className={classes.timeInputBox} mt={1} mb={1}>
                      <FormControl variant="outlined">
                        <InputLabel id="offeredShiftStartHourSelectLabel">Hr</InputLabel>
                        <Select
                          labelId="offeredShiftStartHourSelectLabel"
                          id="offeredShiftStartHourSelectElement"
                          className={classes.timeInput}
                          value={props.offeredShiftStartHour}
                          onChange={handleOfferedShiftStartHourChange}
                          label="Hr"
                        >
                          <MenuItem value="" disabled>
                            <em>Hour</em>
                          </MenuItem>
                          {renderHours()}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box className={classes.timeColonBox} mt={1} mb={1}>
                      :
                    </Box>
                    <Box className={classes.timeInputBox} mt={1} mb={1}>
                      <FormControl variant="outlined">
                        <InputLabel id="offeredShiftStartMinuteSelectLabel">Min</InputLabel>
                        <Select
                          labelId="offeredShiftStartMinuteSelectLabel"
                          id="offeredShiftStartMinuteSelectElement"
                          className={classes.timeInput}
                          value={props.offeredShiftStartMinute}
                          onChange={handleOfferedShiftStartMinuteChange}
                          label="Min"
                        >
                          <MenuItem value="" disabled>
                            <em>Minute</em>
                          </MenuItem>
                          {renderMinutes()}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>

          <Grid className={classes.datetimeInputGrid} item xs={12}>
            <Box className={classes.datetimeInputBox} m={1}>
              <StarsDatePicker
                label="End Date/Time"
                date={props.offeredShiftEnd}
                onChange={handleOfferedShiftEndChange}
                dateErrors={props.offeredShiftEndErrors}
                minDate={convert_Luxon_To_DateFns(
                  props.isAdminCreateTrade ? TWO_MONTHS_IN_PAST(props.station) : TODAY(props.station)
                )}
                className={classes.dateInput}
              />
            </Box>

            <Box className={classes.datetimeInputBox} mt={1} mb={1} mr={1}>
              <Grid container className={classes.dateInputBox}>
                {props.offeredShiftEndTimeErrors !== "" ? (
                  <Grid item xs={12} className={classes.messageBorder}>
                    <Box className={classes.timeInputBox}>
                      <FormControl variant="outlined">
                        <InputLabel id="offeredShiftEndHourSelectLabel" error>
                          Hr
                        </InputLabel>
                        <Select
                          labelId="offeredShiftEndHourSelectLabel"
                          id="offeredShiftEndHourSelectElement"
                          className={classes.timeInput}
                          value={props.offeredShiftEndHour}
                          onChange={handleOfferedShiftEndHourChange}
                          label="Hr"
                          error
                        >
                          <MenuItem value="" disabled>
                            <em>Hour</em>
                          </MenuItem>
                          {renderHours()}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box className={classes.timeColonBox}>:</Box>
                    <Box className={classes.timeInputBox}>
                      <FormControl variant="outlined">
                        <InputLabel id="offeredShiftEndMinuteSelectLabel" error>
                          Min
                        </InputLabel>
                        <Select
                          labelId="offeredShiftEndMinuteSelectLabel"
                          id="offeredShiftEndMinuteSelectElement"
                          className={classes.timeInput}
                          value={props.offeredShiftEndMinute}
                          onChange={handleOfferedShiftEndMinuteChange}
                          label="Min"
                          error
                        >
                          <MenuItem value="" disabled>
                            <em>Minute</em>
                          </MenuItem>
                          {renderMinutes()}
                        </Select>
                      </FormControl>
                    </Box>
                    <FormHelperText className={classes.warningFormat} error>
                      {props.offeredShiftEndTimeErrors}
                    </FormHelperText>
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <Box className={classes.timeInputBox} mt={1} mb={1}>
                      <FormControl variant="outlined">
                        <InputLabel id="offeredShiftEndHourSelectLabel">Hr</InputLabel>
                        <Select
                          labelId="offeredShiftEndHourSelectLabel"
                          id="offeredShiftEndHourSelectElement"
                          className={classes.timeInput}
                          value={props.offeredShiftEndHour}
                          onChange={handleOfferedShiftEndHourChange}
                          label="Hr"
                        >
                          <MenuItem value="" disabled>
                            <em>Hour</em>
                          </MenuItem>
                          {renderHours()}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box className={classes.timeColonBox} mt={1} mb={1}>
                      :
                    </Box>
                    <Box className={classes.timeInputBox} mt={1} mb={1}>
                      <FormControl variant="outlined">
                        <InputLabel id="offeredShiftEndMinuteSelectLabel">Min</InputLabel>
                        <Select
                          labelId="offeredShiftEndMinuteSelectLabel"
                          id="offeredShiftEndMinuteSelectElement"
                          className={classes.timeInput}
                          value={props.offeredShiftEndMinute}
                          onChange={handleOfferedShiftEndMinuteChange}
                          label="Min"
                        >
                          <MenuItem value="" disabled>
                            <em>Minute</em>
                          </MenuItem>
                          {renderMinutes()}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>

          {props.isOfferedShiftDSTWarningOpen && (
            <Grid container xs={12} sx={{ justifyContent: "center" }}>
              <Alert severity="warning" sx={{ maxWidth: "350px", textAlign: "start" }}>
                {DST_WARNING}
              </Alert>
            </Grid>
          )}

          <Box mt={2} mb={1}>
            {props.offeredReliefShiftErrors !== "" ? (
              <FormControl variant="outlined">
                <InputLabel id="offeredReliefShiftSelectLabel" error>
                  Relief Shift
                </InputLabel>
                <Select
                  labelId="offeredReliefShiftSelectLabel"
                  id="offeredReliefShiftSelectElement"
                  className="select-field"
                  value={props.isOfferedReliefShift}
                  onChange={handleOfferedReliefShiftSelectChange}
                  label="Relief Shift"
                  error
                >
                  <MenuItem value="" disabled>
                    <em>Relief Shift</em>
                  </MenuItem>
                  <MenuItem key="No" value={false}>
                    No
                  </MenuItem>
                  <MenuItem key="Yes" value={true}>
                    Yes
                  </MenuItem>
                </Select>
                <FormHelperText error>{props.offeredReliefShiftErrors}</FormHelperText>
              </FormControl>
            ) : (
              <FormControl variant="outlined">
                <InputLabel id="offeredReliefShiftSelectLabel">Relief Shift</InputLabel>
                <Select
                  labelId="offeredReliefShiftSelectLabel"
                  id="offeredReliefShiftSelectElement"
                  className="select-field"
                  value={props.isOfferedReliefShift}
                  onChange={handleOfferedReliefShiftSelectChange}
                  label="Relief Shift"
                >
                  <MenuItem value="" disabled>
                    <em>Relief Shift</em>
                  </MenuItem>
                  <MenuItem key="No" value={false}>
                    No
                  </MenuItem>
                  <MenuItem key="Yes" value={true}>
                    Yes
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </Grid>
        <Grid item xs={12}>
          {props.isAdminCreateTrade ? (
            <div>
              <Divider className="divider" />

              <div className="section-title-left">Employee #2</div>
              <Box mt={1} mb={1}>
                {props.requestedEmployeeIdErrors !== "" ? (
                  <TextField
                    className="text-field"
                    label="Employee ID"
                    variant="outlined"
                    value={props.requestedEmployeeId}
                    onChange={props.onEmpTwoChange}
                    onBlur={props.onEmpTwoBlur}
                    onKeyUp={props.onEmpTwoEnterKeyPressed}
                    error
                    InputProps={{
                      endAdornment:
                        props.requestedEmployeeId !== "" ? (
                          <InputAdornment position="start">
                            <CancelButtonEmpTwo />
                          </InputAdornment>
                        ) : (
                          ""
                        ),
                    }}
                    helperText={props.requestedEmployeeIdErrors}
                  />
                ) : (
                  <TextField
                    className="text-field"
                    label="Employee ID"
                    variant="outlined"
                    value={props.requestedEmployeeId}
                    onChange={props.onEmpTwoChange}
                    onBlur={props.onEmpTwoBlur}
                    onKeyUp={props.onEmpTwoEnterKeyPressed}
                    InputProps={{
                      endAdornment:
                        props.requestedEmployeeId !== "" ? (
                          <InputAdornment position="start">
                            <CancelButtonEmpTwo />
                          </InputAdornment>
                        ) : (
                          ""
                        ),
                    }}
                  />
                )}
              </Box>
              {props.requestedEmployeeSearchTriggered ? (
                props.requestedEmployeeSearchLoaded ? (
                  props.requestedEmployeeLastName !== "" &&
                  props.requestedEmployeeFirstName !== "" &&
                  props.requestedEmployeeStation !== "" &&
                  props.requestedEmployeeWorkgroup !== "" ? (
                    <Grid className="manage-access-employee-text" item xs={12}>
                      <div>
                        <span className="bold-text">Name:</span>{" "}
                        {props.requestedEmployeeLastName + ", " + props.requestedEmployeeFirstName}
                      </div>
                      {props.requestedEmployeeTempStation && (
                        <div>
                          <span className="bold-text">Temp Station:</span> {props.requestedEmployeeTempStation} (Active)
                        </div>
                      )}
                      <div>
                        <span className="bold-text">Home Station:</span> {props.requestedEmployeeHomeStation}
                      </div>
                      <div>
                        <span className="bold-text">Workgroup:</span> {props.requestedEmployeeWorkgroup}
                      </div>
                    </Grid>
                  ) : (
                    ""
                  )
                ) : (
                  <CircularProgress />
                )
              ) : (
                ""
              )}
            </div>
          ) : (
            <div>
              <Divider className="divider" />
              <div className="section-title-left">Offered To</div>
              <Box mt={1} mb={1}>
                <FormControl variant="outlined">
                  <Autocomplete
                    key={props.requestedEmployeeAutocompleteKey}
                    id="empTwoSelectElement"
                    className="select-field"
                    onChange={handleEmpTwoSelectChange}
                    options={eligibleEmployees}
                    groupBy={(option) => option.FullName.slice(0, 1)}
                    getOptionLabel={(option) => getEligibleEmployeeLabel(option)}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        {getEligibleEmployeeLabel(option)}
                        {option.isTemp && <Chip className={classes.tempChip} label={EMPLOYEE_TEMP_LABEL} />}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Employee"
                        variant="outlined"
                        error={props.requestedEmployeeErrors !== ""}
                      />
                    )}
                    loading={isLoading}
                  />
                  {props.requestedEmployeeErrors !== "" && (
                    <FormHelperText error>{props.requestedEmployeeErrors}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              <Divider className="divider" />
              <div className="section-title-left">Shift Requested</div>
            </div>
          )}

          <Grid className={classes.datetimeInputGrid} item xs={12}>
            <Box className={classes.datetimeInputBox} m={1}>
              <StarsDatePicker
                label="Start Date/Time"
                date={props.requestedShiftStart}
                onChange={handleRequestedShiftStartChange}
                dateErrors={props.requestedShiftStartErrors}
                minDate={convert_Luxon_To_DateFns(
                  props.isAdminCreateTrade ? TWO_MONTHS_IN_PAST(props.station) : TODAY(props.station)
                )}
                maxDate={convert_Luxon_To_DateFns(THREE_MONTHS_IN_FUTURE(props.station))}
                className={classes.dateInput}
              />
            </Box>

            <Box className={classes.datetimeInputBox} mt={1} mb={1} mr={1}>
              <Grid container className={classes.dateInputBox}>
                {props.requestedShiftStartTimeErrors !== "" ? (
                  <Grid item xs={12} className={classes.messageBorder}>
                    <Box className={classes.timeInputBox}>
                      <FormControl variant="outlined">
                        <InputLabel id="requestedShiftStartHourSelectLabel" error>
                          Hr
                        </InputLabel>
                        <Select
                          labelId="requestedShiftStartHourSelectLabel"
                          id="requestedShiftStartHourSelectElement"
                          className={classes.timeInput}
                          value={props.requestedShiftStartHour}
                          onChange={handleRequestedShiftStartHourChange}
                          label="Hr"
                          error
                        >
                          <MenuItem value="" disabled>
                            <em>Hour</em>
                          </MenuItem>
                          {renderHours()}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box className={classes.timeColonBox}>:</Box>
                    <Box className={classes.timeInputBox}>
                      <FormControl variant="outlined">
                        <InputLabel id="requestedShiftStartMinuteSelectLabel" error>
                          Min
                        </InputLabel>
                        <Select
                          labelId="requestedShiftStartMinuteSelectLabel"
                          id="requestedShiftStartMinuteSelectElement"
                          className={classes.timeInput}
                          value={props.requestedShiftStartMinute}
                          onChange={handleRequestedShiftStartMinuteChange}
                          label="Min"
                          error
                        >
                          <MenuItem value="" disabled>
                            <em>Minute</em>
                          </MenuItem>
                          {renderMinutes()}
                        </Select>
                      </FormControl>
                    </Box>
                    <FormHelperText className={classes.warningFormat} error>
                      {props.requestedShiftStartTimeErrors}
                    </FormHelperText>
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <Box className={classes.timeInputBox} mt={1} mb={1}>
                      <FormControl variant="outlined">
                        <InputLabel id="requestedShiftStartHourSelectLabel">Hr</InputLabel>
                        <Select
                          labelId="requestedShiftStartHourSelectLabel"
                          id="requestedShiftStartHourSelectElement"
                          className={classes.timeInput}
                          value={props.requestedShiftStartHour}
                          onChange={handleRequestedShiftStartHourChange}
                          label="Hr"
                        >
                          <MenuItem value="" disabled>
                            <em>Hour</em>
                          </MenuItem>
                          {renderHours()}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box className={classes.timeColonBox} mt={1} mb={1}>
                      :
                    </Box>
                    <Box className={classes.timeInputBox} mt={1} mb={1}>
                      <FormControl variant="outlined">
                        <InputLabel id="requestedShiftStartMinuteSelectLabel">Min</InputLabel>
                        <Select
                          labelId="requestedShiftStartMinuteSelectLabel"
                          id="requestedShiftStartMinuteSelectElement"
                          className={classes.timeInput}
                          value={props.requestedShiftStartMinute}
                          onChange={handleRequestedShiftStartMinuteChange}
                          label="Min"
                        >
                          <MenuItem value="" disabled>
                            <em>Minute</em>
                          </MenuItem>
                          {renderMinutes()}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>

          <Grid className={classes.datetimeInputGrid} item xs={12}>
            <Box className={classes.datetimeInputBox} m={1}>
              <StarsDatePicker
                label="End Date/Time"
                date={props.requestedShiftEnd}
                onChange={handleRequestedShiftEndChange}
                dateErrors={props.requestedShiftEndErrors}
                minDate={convert_Luxon_To_DateFns(
                  props.isAdminCreateTrade ? TWO_MONTHS_IN_PAST(props.station) : TODAY(props.station)
                )}
                className={classes.dateInput}
              />
            </Box>

            <Box className={classes.datetimeInputBox} mt={1} mb={1} mr={1}>
              <Grid container className={classes.dateInputBox}>
                {props.requestedShiftEndTimeErrors !== "" ? (
                  <Grid item xs={12} className={classes.messageBorder}>
                    <Box className={classes.timeInputBox}>
                      <FormControl variant="outlined">
                        <InputLabel id="requestedShiftEndHourSelectLabel" error>
                          Hr
                        </InputLabel>
                        <Select
                          labelId="requestedShiftEndHourSelectLabel"
                          id="requestedShiftEndHourSelectElement"
                          className={classes.timeInput}
                          value={props.requestedShiftEndHour}
                          onChange={handleRequestedShiftEndHourChange}
                          label="Hr"
                          error
                        >
                          <MenuItem value="" disabled>
                            <em>Hour</em>
                          </MenuItem>
                          {renderHours()}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box className={classes.timeColonBox}>:</Box>
                    <Box className={classes.timeInputBox}>
                      <FormControl variant="outlined">
                        <InputLabel id="requestedShiftEndMinuteSelectLabel" error>
                          Min
                        </InputLabel>
                        <Select
                          labelId="requestedShiftEndMinuteSelectLabel"
                          id="requestedShiftEndMinuteSelectElement"
                          className={classes.timeInput}
                          value={props.requestedShiftEndMinute}
                          onChange={handleRequestedShiftEndMinuteChange}
                          label="Min"
                          error
                        >
                          <MenuItem value="" disabled>
                            <em>Minute</em>
                          </MenuItem>
                          {renderMinutes()}
                        </Select>
                      </FormControl>
                    </Box>
                    <FormHelperText className={classes.warningFormat} error>
                      {props.requestedShiftEndTimeErrors}
                    </FormHelperText>
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <Box className={classes.timeInputBox} mt={1} mb={1}>
                      <FormControl variant="outlined">
                        <InputLabel id="requestedShiftEndHourSelectLabel">Hr</InputLabel>
                        <Select
                          labelId="requestedShiftEndHourSelectLabel"
                          id="requestedShiftEndHourSelectElement"
                          className={classes.timeInput}
                          value={props.requestedShiftEndHour}
                          onChange={handleRequestedShiftEndHourChange}
                          label="Hr"
                        >
                          <MenuItem value="" disabled>
                            <em>Hour</em>
                          </MenuItem>
                          {renderHours()}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box className={classes.timeColonBox} mt={1} mb={1}>
                      :
                    </Box>
                    <Box className={classes.timeInputBox} mt={1} mb={1}>
                      <FormControl variant="outlined">
                        <InputLabel id="requestedShiftEndMinuteSelectLabel">Min</InputLabel>
                        <Select
                          labelId="requestedShiftEndMinuteSelectLabel"
                          id="requestedShiftEndMinuteSelectElement"
                          className={classes.timeInput}
                          value={props.requestedShiftEndMinute}
                          onChange={handleRequestedShiftEndMinuteChange}
                          label="Min"
                        >
                          <MenuItem value="" disabled>
                            <em>Minute</em>
                          </MenuItem>
                          {renderMinutes()}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>

          {props.isRequestedShiftDSTWarningOpen && (
            <Grid container xs={12} sx={{ justifyContent: "center" }}>
              <Alert severity="warning" sx={{ maxWidth: "350px", textAlign: "start" }}>
                {DST_WARNING}
              </Alert>
            </Grid>
          )}

          <Box mt={2} mb={1}>
            {props.requestedReliefShiftErrors !== "" ? (
              <FormControl variant="outlined">
                <InputLabel id="requestedReliefShiftSelectLabel" error>
                  Relief Shift
                </InputLabel>
                <Select
                  labelId="requestedReliefShiftSelectLabel"
                  id="requestedReliefShiftSelectElement"
                  className="select-field"
                  value={props.isRequestedReliefShift}
                  onChange={handleRequestedReliefShiftSelectChange}
                  label="Relief Shift"
                  error
                >
                  <MenuItem value="" disabled>
                    <em>Relief Shift</em>
                  </MenuItem>
                  <MenuItem key="No" value={false}>
                    No
                  </MenuItem>
                  <MenuItem key="Yes" value={true}>
                    Yes
                  </MenuItem>
                </Select>
                <FormHelperText error>{props.requestedReliefShiftErrors}</FormHelperText>
              </FormControl>
            ) : (
              <FormControl variant="outlined">
                <InputLabel id="requestedReliefShiftSelectLabel">Relief Shift</InputLabel>
                <Select
                  labelId="requestedReliefShiftSelectLabel"
                  id="requestedReliefShiftSelectElement"
                  className="select-field"
                  value={props.isRequestedReliefShift}
                  onChange={handleRequestedReliefShiftSelectChange}
                  label="Relief Shift"
                >
                  <MenuItem value="" disabled>
                    <em>Relief Shift</em>
                  </MenuItem>
                  <MenuItem key="No" value={false}>
                    No
                  </MenuItem>
                  <MenuItem key="Yes" value={true}>
                    Yes
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </Grid>

        {props.isAdminCreateTrade ? (
          <Grid item xs={12}>
            <Divider className="divider" />
            <Box mt={1} mb={1} className="comments-box">
              {props.commentsTouched && props.commentsErrors !== "" ? (
                <TextField
                  error
                  className="text-field"
                  label="Comments"
                  variant="outlined"
                  value={props.comments}
                  onChange={handleCommentsChange}
                  onBlur={handleCommentsBlur}
                  helperText={props.commentsErrors}
                  InputProps={{
                    endAdornment:
                      props.comments !== "" ? (
                        <InputAdornment position="start">
                          <CancelButtonComments />
                        </InputAdornment>
                      ) : (
                        ""
                      ),
                  }}
                  multiline
                />
              ) : (
                <TextField
                  className="text-field"
                  label="Comments"
                  variant="outlined"
                  value={props.comments}
                  onChange={handleCommentsChange}
                  onBlur={handleCommentsBlur}
                  InputProps={{
                    endAdornment:
                      props.comments !== "" ? (
                        <InputAdornment position="start">
                          <CancelButtonComments />
                        </InputAdornment>
                      ) : (
                        ""
                      ),
                  }}
                  multiline
                />
              )}
              {props.comments.length > FREEFORM_REASON_CHAR_LIMIT ? (
                <div className="character-limit-label character-limit-label-red">
                  {props.comments.length} / {FREEFORM_REASON_CHAR_LIMIT}
                </div>
              ) : (
                <div className="character-limit-label">
                  {props.comments.length} / {FREEFORM_REASON_CHAR_LIMIT}
                </div>
              )}
            </Box>
          </Grid>
        ) : (
          ""
        )}

        <Box mt={1} mb={1} className="trade-request-box">
          {props.isAdminCreateTrade ? "" : <UnionContractCutoffTimeReminder pageType={TRADE_PAGE_TYPE} />}
        </Box>
      </Grid>
    </div>
  );
};

TimeTradeForm.propTypes = {
  // Both Admin and Employee Props
  isAdminCreateTrade: PropTypes.bool.isRequired,
  station: PropTypes.string.isRequired,
  jobCode: PropTypes.string.isRequired,
  offeredEmployeeId: PropTypes.string.isRequired,
  offeredShiftStart: PropTypes.object, // isRequired but can be null
  offeredShiftStartErrors: PropTypes.string.isRequired,
  offeredShiftStartHour: PropTypes.string.isRequired,
  offeredShiftStartMinute: PropTypes.string.isRequired,
  offeredShiftStartTimeErrors: PropTypes.string.isRequired,
  offeredShiftEnd: PropTypes.object, // isRequired but can be null
  offeredShiftEndErrors: PropTypes.string.isRequired,
  offeredShiftEndHour: PropTypes.string.isRequired,
  offeredShiftEndMinute: PropTypes.string.isRequired,
  offeredShiftEndTimeErrors: PropTypes.string.isRequired,
  isOfferedReliefShift: PropTypes.bool.isRequired,
  offeredReliefShiftErrors: PropTypes.string.isRequired,
  requestedEmployeeId: PropTypes.string.isRequired,
  requestedShiftStart: PropTypes.object, // isRequired but can be null
  requestedShiftStartErrors: PropTypes.string.isRequired,
  requestedShiftStartHour: PropTypes.string.isRequired,
  requestedShiftStartMinute: PropTypes.string.isRequired,
  requestedShiftStartTimeErrors: PropTypes.string.isRequired,
  requestedShiftEnd: PropTypes.object, // isRequired but can be null
  requestedShiftEndErrors: PropTypes.string.isRequired,
  requestedShiftEndHour: PropTypes.string.isRequired,
  requestedShiftEndMinute: PropTypes.string.isRequired,
  requestedShiftEndTimeErrors: PropTypes.string.isRequired,
  isRequestedReliefShift: PropTypes.bool.isRequired,
  requestedReliefShiftErrors: PropTypes.string.isRequired,
  onEmpOneChange: PropTypes.func.isRequired,
  onOfferedShiftStartChange: PropTypes.func.isRequired,
  onOfferedShiftStartHourChange: PropTypes.func.isRequired,
  onOfferedShiftStartMinuteChange: PropTypes.func.isRequired,
  onOfferedShiftEndChange: PropTypes.func.isRequired,
  onOfferedShiftEndHourChange: PropTypes.func.isRequired,
  onOfferedShiftEndMinuteChange: PropTypes.func.isRequired,
  onOfferedReliefShiftSelectChange: PropTypes.func.isRequired,
  onEmpTwoChange: PropTypes.func.isRequired,
  onRequestedShiftStartChange: PropTypes.func.isRequired,
  onRequestedShiftStartHourChange: PropTypes.func.isRequired,
  onRequestedShiftStartMinuteChange: PropTypes.func.isRequired,
  onRequestedShiftEndChange: PropTypes.func.isRequired,
  onRequestedShiftEndHourChange: PropTypes.func.isRequired,
  onRequestedShiftEndMinuteChange: PropTypes.func.isRequired,
  onRequestedReliefShiftSelectChange: PropTypes.func.isRequired,
  isOfferedShiftDSTWarningOpen: PropTypes.bool,
  isRequestedShiftDSTWarningOpen: PropTypes.bool,

  // Employee Props only
  requestedEmployeeErrors: PropTypes.string,
  requestedEmployeeAutocompleteKey: PropTypes.string,

  // Admin Props only
  offeredEmployeeIdErrors: PropTypes.string,
  offeredEmployeeSearchTriggered: PropTypes.bool,
  offeredEmployeeSearchLoaded: PropTypes.bool,
  offeredEmployeeFirstName: PropTypes.string,
  offeredEmployeeLastName: PropTypes.string,
  offeredEmployeeWorkgroup: PropTypes.string,
  offeredEmployeeTempStation: PropTypes.string,
  offeredEmployeeHomeStation: PropTypes.string,
  requestedEmployeeIdErrors: PropTypes.string,
  requestedEmployeeSearchTriggered: PropTypes.bool,
  requestedEmployeeSearchLoaded: PropTypes.bool,
  requestedEmployeeLastName: PropTypes.string,
  requestedEmployeeFirstName: PropTypes.string,
  requestedEmployeeWorkgroup: PropTypes.string,
  requestedEmployeeTempStation: PropTypes.string,
  requestedEmployeeHomeStation: PropTypes.string,
  comments: PropTypes.string,
  commentsTouched: PropTypes.bool,
  commentsErrors: PropTypes.string,
  onEmpOneClear: PropTypes.func,
  onEmpOneBlur: PropTypes.func,
  onEmpOneEnterKeyPressed: PropTypes.func,
  onEmpTwoClear: PropTypes.func,
  onEmpTwoBlur: PropTypes.func,
  onEmpTwoEnterKeyPressed: PropTypes.func,
  onCommentsClear: PropTypes.func,
  onCommentsChange: PropTypes.func,
  onCommentsBlur: PropTypes.func,
};

export default TimeTradeForm;
